import React from 'react'
import MixedContentPreview, { MathpixScope } from '../UiElements/MixedContentPreview'
import ExplainVisual from './ExplainVisual'
import {
  explainStatusLabel,
  explainSessionQuestionText,
  formatExplainTimestamp
} from '../../helpers/explainSessions'
import './ExplainSessionView.css'
import './ExplainSessionReview.css'

/**
 * Read-only view of a finished or in-flight explain session. Shared by the
 * student's View Work tab and the parent's Explain panel, which is what keeps
 * the two surfaces from drifting apart. It renders what was already loaded and
 * never writes, so neither caller can mutate AI output through it.
 */
const ExplainSessionReview = ({
  session,
  showChat = false,
  chat = [],
  chatState = 'idle',
  chatError = null,
  onRetryChat,
  diagnosis = null,
  headerActions = null
}) => {
  if (!session) return null

  const { steps, status } = session
  const statusLabel = explainStatusLabel(status)
  const isCompleted = status === 'completed'
  // A session the student never finished stops at the step they had reached;
  // marking the rest keeps a parent from reading unseen steps as taught.
  const revealedThrough = isCompleted
    ? steps.length - 1
    : session.currentStepIndex
  const startedAt = formatExplainTimestamp(session.createdAt)
  const finishedAt = formatExplainTimestamp(session.completedAt)

  return (
    // A reviewed session renders a preview per step plus one per transcript
    // message, so the Mathpix stylesheet is installed once for all of them.
    <MathpixScope>
      <div className="explain-session explain-review">
        <div className="explain-paper">
          <div className="explain-paper-header">
            <div className="explain-paper-heading">
              <span className="explain-paper-eyebrow">Step-by-step walkthrough</span>
              {session.topic && <span className="explain-topic-tag">{session.topic}</span>}
              <span className={`explain-review-status ${status}`}>{statusLabel}</span>
            </div>
            {headerActions}
          </div>

          {(startedAt || finishedAt) && (
            <div className="explain-review-meta">
              {startedAt && <span>Started {startedAt}</span>}
              {finishedAt && <span>Finished {finishedAt}</span>}
            </div>
          )}

          <div className="explain-problem">
            <div className="explain-block-label">Problem</div>
            <div className="explain-math-block">
              <MixedContentPreview
                content={explainSessionQuestionText(session)}
                fontSize="1.25rem"
              />
            </div>
          </div>

          {steps.length === 0 ? (
            <div className="explain-review-empty">
              This session has no recorded steps.
            </div>
          ) : (
            // A finished session has no "current step", so every step gets the
            // same neutral surface: no dimming, no highlight. How far the
            // student actually got is carried by the "Not reached" flag.
            <ol className="explain-steps" aria-label="Solution steps">
              {steps.map((step, index) => {
                const reached = index <= revealedThrough
                return (
                  <li key={index} className="explain-step">
                    <div className="explain-step-number">
                      Step {index + 1}
                      {!reached && (
                        <span className="explain-review-step-flag">Not reached</span>
                      )}
                    </div>
                    <div className="explain-math-block">
                      <MixedContentPreview content={step.solution} fontSize="1.1rem" />
                    </div>
                    <ExplainVisual visual={step.visual} />
                    {step.explanation && (
                      <p className="explain-step-explanation">{step.explanation}</p>
                    )}
                  </li>
                )
              })}
            </ol>
          )}

          {session.finalAnswer && (
            <div className="explain-final-answer">
              <div className="explain-block-label">Final answer</div>
              <div className="explain-math-block">
                <MixedContentPreview content={session.finalAnswer} fontSize="1.25rem" />
              </div>
              <ExplainVisual visual={session.finalVisual} />
            </div>
          )}
        </div>

        {showChat && (
          <div className="explain-review-panel">
            <div className="explain-block-label">Questions asked during this session</div>
            {chatState === 'loading' && (
              <div className="explain-review-panel-note">Loading conversation...</div>
            )}
            {chatState === 'error' && (
              <div className="explain-review-panel-error" role="alert">
                <span>{chatError || 'We could not load this conversation.'}</span>
                {onRetryChat && (
                  <button
                    type="button"
                    className="explain-review-retry-btn"
                    onClick={onRetryChat}
                  >
                    Try again
                  </button>
                )}
              </div>
            )}
            {chatState === 'loaded' && chat.length === 0 && (
              <div className="explain-review-panel-note">
                No questions were asked during this session.
              </div>
            )}
            {chatState === 'loaded' && chat.length > 0 && (
              <div className="explain-review-chat">
                {chat.map(message => (
                  <div
                    key={message.id}
                    className={`explain-review-message ${message.role}`}
                  >
                    <div className="explain-review-message-header">
                      <span className="explain-review-message-role">
                        {message.role === 'user' ? 'Student' : 'AI Tutor'}
                      </span>
                      {message.stepIndex !== null && (
                        <span className="explain-review-message-step">
                          Step {message.stepIndex + 1}
                        </span>
                      )}
                    </div>
                    <div className="explain-review-message-content">
                      <MixedContentPreview content={message.content} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {diagnosis && (
          <div className="explain-review-panel">
            <div className="explain-block-label">What this session showed</div>
            <p className="explain-review-diagnosis">{diagnosis.diagnosis}</p>
            {diagnosis.topics.length > 0 && (
              <div className="explain-review-topics">
                {diagnosis.topics.map(topic => (
                  <span key={topic} className="explain-review-topic-chip">{topic}</span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </MathpixScope>
  )
}

export default ExplainSessionReview
